/**
 * Fleet UX: fleet list — quick category filters with counts, status filter, name search, sorting,
 * and cards that show category, composition, place, task, destination, ETA, strength and damage.
 * Rows are computed once per data change; cards are memoized and only the ones with a countdown
 * re-render every second.
 */
import { memo, useEffect, useMemo } from 'react';
import type { FleetClient, FleetGroupClient } from '@galaxis/shared';
import {
  CATEGORY_META, CATEGORY_FILTERS, type FleetCategory, STATUS_FILTER_LABELS, SORT_LABELS, categoryCounts, selectRows, shipKinds, orderProgress,
  type CategoryFilter, type StatusFilter, type SortKey, type FleetRow,
} from './fleet-category.ts';
import { STATUS_LABELS, STATUS_COLORS, formatDuration, taskText } from './fleet-format.ts';
import { FleetCategoryIcon, CompositionChips, useNow } from './FleetIcons.tsx';
import { FleetFormation } from '../../graphics/ShipArt.tsx';
import type { Role } from '../../design/tokens.ts';
import { GroupDot } from './FleetGroups.tsx';
import { groupCounts } from './fleet-overview.ts';

const CATEGORY_ROLE: Record<FleetCategory, Role> = { survey: 'survey', construction: 'build', military: 'military', mixed: 'diplomacy', empty: 'neutral' };

export interface FleetListState { category: CategoryFilter; status: StatusFilter; query: string; sort: SortKey; /** 'any' | 'none' | group id */ group: string }

interface FleetListProps {
  rows: FleetRow[];
  state: FleetListState;
  onState: (s: FleetListState) => void;
  selectedId: string | null;
  onSelect: (id: string) => void;
  systemName: (id: string | null) => string;
  groups: FleetGroupClient[];
  onManageGroups: () => void;
  /** Multi-select mode: cards toggle the selection instead of opening the details */
  selectMode: boolean;
  selection: ReadonlySet<string>;
  onToggleSelect: (id: string) => void;
  onSelectMode: (on: boolean) => void;
  /** Reports the currently visible ids (for "select all visible") */
  onVisible: (ids: string[]) => void;
}

const SELECT = 'rounded-lg bg-slate-900 border border-slate-600 px-2 text-sm text-slate-200 min-w-0';

export function FleetList({ rows, state, onState, selectedId, onSelect, systemName, groups, onManageGroups, selectMode, selection, onToggleSelect, onSelectMode, onVisible }: FleetListProps) {
  const counts = useMemo(() => categoryCounts(rows), [rows]);
  const visible = useMemo(() => selectRows(rows, state), [rows, state]);
  const set = (patch: Partial<FleetListState>) => onState({ ...state, ...patch });
  const gc = useMemo(() => groupCounts(rows.map(r => r.fleet), groups), [rows, groups]);
  const groupById = useMemo(() => new Map(groups.map(g => [g.id, g])), [groups]);
  const visibleKey = visible.map(r => r.fleet.id).join(',');
  useEffect(() => { onVisible(visible.map(r => r.fleet.id)); }, [visibleKey]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="space-y-3 min-w-0">
      <div className="flex items-start justify-between gap-2">
        <div>
          <h1 className="text-lg font-semibold text-slate-200">Flották</h1>
          <p className="text-xs text-slate-500">{rows.length} flotta összesen</p>
        </div>
        <div className="flex gap-2">
          <button type="button" onClick={onManageGroups} data-testid="groups-manage" className="rounded-lg border border-slate-600 text-slate-300 text-xs px-3" style={{ minHeight: 44 }}>🏷 Csoportok</button>
          <button type="button" onClick={() => onSelectMode(!selectMode)} aria-pressed={selectMode} data-testid="select-mode"
            className="rounded-lg border text-xs px-3" style={{ minHeight: 44, color: selectMode ? '#00e5ff' : '#cbd5e1', borderColor: selectMode ? 'rgba(0,229,255,0.7)' : 'rgb(71,85,105)' }}>
            {selectMode ? '✓ Kijelölés' : '☐ Kijelölés'}
          </button>
        </div>
      </div>

      {groups.length > 0 && (
        <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1" role="group" aria-label="Csoportszűrő" data-testid="group-filters">
          {[{ id: 'any', name: 'Minden csoport', color: '#94a3b8', n: rows.length }, ...groups.map(g => ({ id: g.id, name: g.name, color: g.color, n: gc.counts.get(g.id) ?? 0 })), { id: 'none', name: 'Csoport nélkül', color: '#64748b', n: gc.none }].map(g => {
            const active = state.group === g.id;
            return (
              <button key={g.id} type="button" data-testid={g.id === 'any' || g.id === 'none' ? `group-filter-${g.id}` : 'group-filter-g'} aria-pressed={active} onClick={() => set({ group: g.id })}
                className="px-3 rounded-full text-xs whitespace-nowrap inline-flex items-center gap-1.5 flex-shrink-0"
                style={{ minHeight: 44, color: active ? g.color : '#94a3b8', background: active ? `${g.color}22` : 'rgba(15,23,42,0.6)', border: `1px solid ${active ? `${g.color}88` : 'rgba(100,116,139,0.3)'}` }}>
                {g.id !== 'any' && g.id !== 'none' && <GroupDot color={g.color} />}
                {g.name} <span className="font-mono text-[11px] opacity-80">{g.n}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* Quick filters: scroll inside the strip on phones, never the page */}
      <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1" role="group" aria-label="Flottaszűrő">
        {CATEGORY_FILTERS.map(f => {
          const active = state.category === f;
          const color = f === 'all' ? '#00e5ff' : CATEGORY_META[f].color;
          return (
            <button
              key={f}
              type="button"
              data-testid={`fleet-filter-${f}`}
              onClick={() => set({ category: f })}
              aria-pressed={active}
              className="px-3 rounded-full text-xs font-medium whitespace-nowrap transition-colors inline-flex items-center gap-1.5 flex-shrink-0"
              style={{
                minHeight: 44,
                background: active ? `${color}22` : 'rgba(15,23,42,0.6)',
                color: active ? color : '#94a3b8',
                border: `1px solid ${active ? `${color}88` : 'rgba(100,116,139,0.3)'}`,
              }}
            >
              {f !== 'all' && <FleetCategoryIcon category={f} kinds={f === 'mixed' ? ['military', 'survey'] : []} size={14} />}
              {f === 'all' ? 'Összes' : CATEGORY_META[f].filterLabel}
              <span className="font-mono text-[11px] opacity-80" data-testid={`fleet-filter-count-${f}`}>{counts[f]}</span>
            </button>
          );
        })}
      </div>

      <div className="grid grid-cols-2 gap-2">
        <input
          type="search"
          aria-label="Keresés flottanévre"
          placeholder="Keresés flottanévre…"
          value={state.query}
          onChange={e => set({ query: e.target.value })}
          className={`${SELECT} px-3 col-span-2`}
          style={{ minHeight: 44 }}
        />
        <select aria-label="Állapot" value={state.status} onChange={e => set({ status: e.target.value as StatusFilter })} className={SELECT} style={{ minHeight: 44 }}>
          {(Object.keys(STATUS_FILTER_LABELS) as StatusFilter[]).map(s => <option key={s} value={s}>{STATUS_FILTER_LABELS[s]}</option>)}
        </select>
        <select aria-label="Rendezés" value={state.sort} onChange={e => set({ sort: e.target.value as SortKey })} className={SELECT} style={{ minHeight: 44 }}>
          {(Object.keys(SORT_LABELS) as SortKey[]).map(s => <option key={s} value={s}>{SORT_LABELS[s]}</option>)}
        </select>
      </div>

      {visible.length === 0 && (
        <div className="sci-fi-panel rounded-xl p-6 text-center text-sm text-slate-500 flex flex-col items-center gap-2 fade-in" data-testid="fleet-list-empty">
          <FleetFormation shipCounts={{}} size={52} />
          {rows.length === 0 ? 'Nincs flottád.' : 'Ebben a szűrőben nincs flotta.'}
        </div>
      )}
      {visible.length > 0 && visible.length < rows.length && (
        <p className="text-xs text-slate-500" data-testid="fleet-list-shown">{visible.length} / {rows.length} flotta látható</p>
      )}

      <ul className="space-y-2" data-testid="fleet-list">
        {visible.map(r => (
          <li key={r.fleet.id} style={{ contentVisibility: 'auto', containIntrinsicSize: '0 132px' }}>
            <FleetCard row={r} selected={selectMode ? selection.has(r.fleet.id) : r.fleet.id === selectedId}
              onSelect={selectMode ? onToggleSelect : onSelect} systemName={systemName} selectMode={selectMode}
              group={r.fleet.groupId ? groupById.get(r.fleet.groupId) : undefined} />
          </li>
        ))}
      </ul>
    </div>
  );
}

const FleetCard = memo(function FleetCard({ row, selected, onSelect, systemName, selectMode, group }: {
  row: FleetRow; selected: boolean; onSelect: (id: string) => void; systemName: (id: string | null) => string; selectMode: boolean; group?: FleetGroupClient;
}) {
  const { fleet, category } = row;
  const meta = CATEGORY_META[category];
  const statusColor = STATUS_COLORS[fleet.status] ?? '#94a3b8';
  const damaged = fleet.stats.damagedShips;
  return (
    <button
      type="button"
      data-testid="fleet-card"
      data-fleet-id={fleet.id}
      data-category={category}
      data-status={fleet.status}
      onClick={() => onSelect(fleet.id)}
      aria-current={selectMode ? undefined : selected}
      aria-pressed={selectMode ? selected : undefined}
      aria-label={`${fleet.name}, ${meta.label}, ${STATUS_LABELS[fleet.status] ?? fleet.status}${group ? `, csoport: ${group.name}` : ''}`}
      className="w-full text-left sci-fi-panel rounded-xl p-3 space-y-2 transition-colors relative overflow-hidden"
      style={{ minHeight: 44, borderColor: selected ? 'rgba(0,229,255,0.6)' : undefined, boxShadow: `inset 3px 0 0 ${meta.color}` }}
    >
      <div className="flex items-center justify-between gap-2 min-w-0">
        <span className="flex items-center gap-2 min-w-0">
          {selectMode && (
            <span aria-hidden data-testid="fleet-check" className="inline-flex items-center justify-center rounded border flex-shrink-0 text-xs"
              style={{ width: 22, height: 22, borderColor: selected ? '#00e5ff' : '#64748b', color: '#00e5ff', background: selected ? 'rgba(0,229,255,0.15)' : 'transparent' }}>{selected ? '✓' : ''}</span>
          )}
          <FleetCategoryIcon category={category} kinds={shipKinds(fleet.stats.shipCounts)} />
          <span className="text-sm font-medium text-slate-100 truncate">{fleet.name}</span>
          {group && <span className="inline-flex items-center gap-1 text-[10px] flex-shrink-0 px-1.5 rounded" data-testid="fleet-group-chip" style={{ color: group.color, border: `1px solid ${group.color}66` }}>{group.name}</span>}
          {fleet.role === 'reserve' && <span className="text-[10px] text-slate-400 uppercase flex-shrink-0">tartalék</span>}
          {fleet.commands.automation && (
            <span className="text-[11px] flex-shrink-0" title={fleet.commands.automation === 'patrol' ? 'Járőrözik' : 'Automatikus felfedezés'}>
              {fleet.commands.automation === 'patrol' ? '🔁' : '🤖'}
            </span>
          )}
        </span>
        <span className="text-xs px-2 py-0.5 rounded flex-shrink-0" data-testid="fleet-status"
          style={{ color: statusColor, background: `${statusColor}15`, border: `1px solid ${statusColor}40` }}>
          {STATUS_LABELS[fleet.status] ?? fleet.status}
        </span>
      </div>

      <div className="flex items-center justify-between gap-2 text-xs">
        <span className="text-[11px] uppercase tracking-wide" style={{ color: meta.color }}>{meta.label}</span>
        <span className="text-slate-500">{fleet.stats.totalShips} hajó</span>
      </div>
      <div className="flex items-center justify-between gap-3">
        <CompositionChips shipCounts={fleet.stats.shipCounts} />
        <FleetFormation
          shipCounts={fleet.stats.shipCounts}
          role={CATEGORY_ROLE[category]}
          size={46}
          damage={fleet.stats.totalShips > 0 ? damaged / fleet.stats.totalShips : 0}
          thrust={fleet.status === 'traveling'}
        />
      </div>

      <div className="text-xs text-slate-400 truncate" data-testid="fleet-location">
        📍 {row.location}
        {fleet.destinationSystemId && <span className="text-cyan-400"> → {systemName(fleet.destinationSystemId)}</span>}
      </div>
      <div className="text-xs text-slate-300 truncate" data-testid="fleet-card-task">
        {taskText(fleet, systemName)}{fleet.commands.queued > 0 && <span className="text-slate-500"> · +{fleet.commands.queued} parancs</span>}
      </div>
      {fleet.activeOrder && fleet.eta && <EtaBar fleet={fleet} color={statusColor} />}

      {(fleet.capabilities.military || damaged > 0) && (
        <div className="flex items-center justify-between text-xs gap-2">
          {fleet.capabilities.military
            ? <span className="font-mono text-red-300/90" title="Becsült katonai erő" data-testid="fleet-strength">⚔ {fleet.stats.militaryStrength}</span>
            : <span />}
          {damaged > 0 && <span className="text-amber-300" data-testid="fleet-damaged">⚠ {damaged} sérült hajó</span>}
        </div>
      )}
    </button>
  );
});

/** The only per-second part of a card. */
function EtaBar({ fleet, color }: { fleet: FleetClient; color: string }) {
  const now = useNow();
  const p = orderProgress(fleet, now);
  const remaining = new Date(fleet.eta!).getTime() - now;
  if (p === null) return null;
  return (
    <div className="space-y-1" data-testid="fleet-eta">
      <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(p * 100)}>
        <div className="h-full rounded-full" style={{ width: `${p * 100}%`, background: color }} />
      </div>
      <div className="text-[11px] text-slate-500 flex justify-between">
        <span>{Math.round(p * 100)}%</span>
        <span className="font-mono text-cyan-400/80">{remaining > 0 ? formatDuration(remaining) : 'érkezik…'}</span>
      </div>
    </div>
  );
}
